import React, { useCallback, type FunctionComponent, type ReactNode } from 'react';
import { useJiraAppChromeQuery } from '@jira/nav-platform-integration__resources/src/useJiraAppChromeQuery.tsx';
import { NavBanner } from '@jira/nav-platform-integration__banner/src/NavBanner.tsx';
import {
	NavPlatformTopNav,
	type NavPlatformTopNavProps,
} from '@jira/nav-platform-integration__topnav/src/NavPlatformTopNav.tsx';
import { useIsChromeless } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useIsFullscreen } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { isStableUIEnabledForTests } from '@jira/platform__is-stable-ui-enabled-for-tests/src/isStableUIEnabledForTests.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import { GlobalRoot } from '@atlassian/global-root';
import { fg } from '@atlassian/jira-feature-gating';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';

type NavPlatformLayoutProps = {
	children: ReactNode;
	isSideNavShortcutEnabled?: boolean;
	SidebarEntry: FunctionComponent;
	testId?: string;
};

/**
 * The Jira Nav Platform layout. Owns the top nav, side nav and banner slot
 * implementations for the Nav Platform code-path (gated by `navx-global-top-nav-jira`)
 * and renders them into the platform `GlobalRoot`.
 *
 * Slot visibility mirrors the legacy page-container layout: banner/top-nav/side-nav
 * are hidden in chromeless/fullscreen modes.
 */
export function NavPlatformLayout(props: NavPlatformLayoutProps): React.JSX.Element {
	const { children, isSideNavShortcutEnabled, SidebarEntry, testId } = props;
	const [isChromeless] = useIsChromeless();
	const [isFullscreen] = useIsFullscreen();
	const isAnonymous = useIsAnonymous();

	const showBanner =
		!isChromeless &&
		!isFullscreen &&
		!(isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion'));
	const showTopNav = !isChromeless && !isFullscreen;
	const showSidebar = !isChromeless && !isFullscreen;

	const BannerSlot = useCallback(
		() =>
			showBanner ? (
				<JSErrorBoundary id="banner" packageName="jiraNavPlatLayout" fallback="unmount">
					<NavBanner />
				</JSErrorBoundary>
			) : null,
		[showBanner],
	);

	const TopNavSlot = useCallback(
		(tnprops: NavPlatformTopNavProps) =>
			showTopNav ? (
				<UFOSegment name="nav4.topNav">
					<JSErrorBoundary id="top-nav" packageName="jiraNavPlatLayout" fallback="unmount">
						<NavPlatformTopNav {...tnprops} />
					</JSErrorBoundary>
				</UFOSegment>
			) : null,
		[showTopNav],
	);

	const SideNavSlot = useCallback(
		() =>
			showSidebar ? (
				<JSErrorBoundary id="sidebar" packageName="jiraNavPlatLayout" fallback="flag">
					<SidebarEntry />
				</JSErrorBoundary>
			) : null,
		[showSidebar, SidebarEntry],
	);

	return (
		<GlobalRoot
			app="jira" // TODO: Confirm what these values are (could be jpd, jsm, ...?)
			appLabel="Jira"
			BannerSlot={BannerSlot}
			customizationStore="network"
			defaultSideNavCollapsed={undefined}
			isAnonymous={isAnonymous}
			isSideNavShortcutEnabled={isSideNavShortcutEnabled}
			isChromeless={isChromeless}
			SideNavSlot={SideNavSlot}
			testId={testId}
			TopNavSlot={TopNavSlot}
			useRelayQuery={useJiraAppChromeQuery}
			UNSAFE_dangerouslyHoistSlotSizes
		>
			{children}
		</GlobalRoot>
	);
}
